import { useEffect, useState } from 'react'
import { AxiosError } from 'axios'
import {
  AdminsApi,
  AdminsEcommerceCreateProductRequest,
  AdminsGetProductListResponse,
  Configuration,
  EcommerceProductDimensionMeasurementEnum,
  EcommerceProductStatusEnum,
  EcommerceProductTypeEnum,
  EcommerceProductUnitOfMeasurementEnum,
} from 'generated/api'
import Toast from 'components/toast/Toast'
import { DropDownEcommerceProductStatusEnum } from 'constants/dropdown'
import { axiosDefaultConfig } from 'constants/axios'
import { enumToastMessage } from 'constants/toast'

const axiosConfig = new Configuration(axiosDefaultConfig)
const adminsApi = new AdminsApi(axiosConfig)

interface Option {
  value: string
  label: string
}

export const useProducts = () => {
  const { toast, toastContainer } = Toast()
  const [statusFetchProducts, setStatusFetchProducts] = useState<'loading' | 'failed' | 'idle'>(
    'loading',
  )
  const [products, setProducts] = useState<AdminsGetProductListResponse[]>([])
  const [currentPageProducts, setCurrentPageProducts] = useState(1)
  const [totalRowsProducts, setTotalRowsProducts] = useState(0)
  const [productStatus, setProductStatus] = useState<DropDownEcommerceProductStatusEnum>(
    DropDownEcommerceProductStatusEnum.ALL,
  )
  const [limitPageProducts, setLimitPageProducts] = useState(10)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchNameField] = useState('name')
  const [productId, setProductId] = useState<string>('')
  const [isOpenModalForm, setIsOpenModalForm] = useState(false)
  const [stateForm, setStateForm] = useState<'CREATE' | 'EDIT'>('CREATE')
  const [initialForm, setInitialForm] = useState<AdminsEcommerceCreateProductRequest>({
    categoryId: '',
    type: EcommerceProductTypeEnum.Goods,
    sku: '',
    status: EcommerceProductStatusEnum.Active,
    name: '',
    description: '',
    detailDescription: '',
    unitOfMeasurement: '' as EcommerceProductUnitOfMeasurementEnum,
    productWeight: 0,
    dimensionMeasurement: '' as EcommerceProductDimensionMeasurementEnum,
    dimensionLength: 0,
    dimensionWidth: 0,
    dimensionHeight: 0,
    imageURL: '',
  })
  const [statusForm, setStatusForm] = useState<'loading' | 'idle' | 'failed'>('idle')
  const [categoriesProduct, setCategoriesProduct] = useState<Option[]>([])

  useEffect(() => {
    fetchProducts()
  }, [currentPageProducts, limitPageProducts, productStatus])

  useEffect(() => {
    if (productId) {
      fetchEditForm()
    }
  }, [productId])

  const fetchProducts = async () => {
    try {
      setStatusFetchProducts('loading')
      const { data } = await adminsApi.adminsEcommerceGetListProduct(
        '',
        productStatus,
        searchKeyword ? searchKeyword : undefined,
        searchKeyword ? searchNameField : undefined,
        undefined,
        currentPageProducts,
        limitPageProducts,
      )
      setStatusFetchProducts('idle')
      setProducts(data.items)
      setTotalRowsProducts(data.pagination.total)
    } catch (err) {
      setStatusFetchProducts('failed')
      setProducts([])
      setTotalRowsProducts(1)
    }
  }

  const changeLimitPageProducts = (limitPage: number) => {
    setCurrentPageProducts(1)
    setLimitPageProducts(limitPage)
  }

  const changeCurrentPageProducts = (page: number) => {
    setCurrentPageProducts(page)
  }

  const changeSearchKeyword = (searchKeyword: string) => {
    setSearchKeyword(searchKeyword)
  }

  const enterSearchKeyword = () => {
    currentPageProducts === 1 ? fetchProducts() : setCurrentPageProducts(1)
  }

  const changeFilterProductStatus = (updatedStatus: DropDownEcommerceProductStatusEnum) => {
    setCurrentPageProducts(1)
    setProductStatus(updatedStatus)
  }

  const resetForm = () => {
    setInitialForm({
      categoryId: '',
      type: EcommerceProductTypeEnum.Goods,
      sku: '',
      status: EcommerceProductStatusEnum.Active,
      name: '',
      description: '',
      detailDescription: '',
      unitOfMeasurement: '' as EcommerceProductUnitOfMeasurementEnum,
      productWeight: 0,
      dimensionMeasurement: '' as EcommerceProductDimensionMeasurementEnum,
      dimensionLength: 0,
      dimensionWidth: 0,
      dimensionHeight: 0,
      imageURL: '',
    })
  }

  const openCreateForm = async () => {
    setStateForm('CREATE')
    setIsOpenModalForm(true)
    await initCreate()
  }

  const openEditForm = async (product: AdminsGetProductListResponse) => {
    setStateForm('EDIT')
    setIsOpenModalForm(true)
    setProductId(product.id)
  }

  const closeForm = () => {
    setIsOpenModalForm(false)
    setProductId('')
    resetForm()
  }

  const initCreate = async () => {
    resetForm()
    setStatusForm('loading')
    await fetchCategories()
    setStatusForm('idle')
  }

  const fetchEditForm = async () => {
    setStatusForm('loading')
    await fetchCategories()
    const { data } = await adminsApi.adminsEcommerceGetProductDetail('', productId)
    setInitialForm({
      sku: data.sku,
      name: data.name,
      description: data.description,
      detailDescription: data.detailDescription,
      categoryId: data.categoryId,
      type: data.type as EcommerceProductTypeEnum,
      status: data.status as EcommerceProductStatusEnum,
      unitOfMeasurement: data.unitOfMeasurement,
      productWeight: data.productWeight,
      dimensionMeasurement: data.dimensionMeasurement,
      dimensionLength: data.dimensionLength,
      dimensionWidth: data.dimensionWidth,
      dimensionHeight: data.dimensionHeight,
      imageURL: data.imageURL,
    })
    setStatusForm('idle')
  }

  const fetchCategories = async () => {
    const { data } = await adminsApi.adminsEcommerceGetProductCategory('', 1, 100, 'ACTIVE')
    const newCategories = data.items.map(newCategory => ({
      label: newCategory.name,
      value: newCategory.id,
    }))
    setCategoriesProduct(newCategories)
  }

  const submitForm = (values: AdminsEcommerceCreateProductRequest) => {
    if (stateForm === 'CREATE') createProduct(values)
    else if (stateForm === 'EDIT') updateProduct(values)
  }

  const payloadForm = (values: AdminsEcommerceCreateProductRequest) => {
    return {
      categoryId: values.categoryId,
      type: values.type,
      sku: values.sku,
      status: values.status,
      name: values.name,
      description: values.description,
      detailDescription: values.detailDescription,
      unitOfMeasurement: values.unitOfMeasurement,
      productWeight: values.productWeight,
      ...(values.dimensionMeasurement !== ('' as EcommerceProductDimensionMeasurementEnum) && {
        dimensionMeasurement: values.dimensionMeasurement,
      }),
      ...(values.dimensionLength !== 0 && { dimensionLength: values.dimensionLength }),
      ...(values.dimensionWidth !== 0 && { dimensionWidth: values.dimensionWidth }),
      ...(values.dimensionHeight !== 0 && { dimensionHeight: values.dimensionHeight }),
      imageURL: values.imageURL,
    }
  }

  const createProduct = async (values: AdminsEcommerceCreateProductRequest) => {
    try {
      setStatusForm('loading')
      const payload = payloadForm(values)
      await adminsApi.adminsEcommerceCreateProduct('', payload)
      setStatusForm('idle')
      closeForm()
      await toast('Produk berhasil ditambahkan', 'success')
      await fetchProducts()
    } catch (err) {
      setStatusForm('failed')
      const error = err as AxiosError
      const responseStatus = error.response?.status
      const expectedResponseStatus = [400, 500]
      if (responseStatus && expectedResponseStatus.includes(responseStatus)) {
        if (responseStatus === 400) {
          await toast(enumToastMessage.err_400, 'danger')
        } else if (responseStatus === 500) {
          await toast(enumToastMessage.err_500, 'danger')
        }
      } else {
        await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
      }
    }
  }

  const updateProduct = async (values: AdminsEcommerceCreateProductRequest) => {
    try {
      setStatusForm('loading')
      const payload = payloadForm(values)
      await adminsApi.adminsEcommerceUpdateProduct('', productId, payload)
      setStatusForm('idle')
      closeForm()
      await toast('Produk berhasil diubah', 'success')
      await fetchProducts()
    } catch (err) {
      setStatusForm('failed')
      const error = err as AxiosError
      const responseStatus = error.response?.status
      const expectedResponseStatus = [400, 500]
      if (responseStatus && expectedResponseStatus.includes(responseStatus)) {
        if (responseStatus === 400) {
          await toast(enumToastMessage.err_400, 'danger')
        } else if (responseStatus === 500) {
          await toast(enumToastMessage.err_500, 'danger')
        }
      } else {
        await toast('Tidak dapat terhubung ke server, silakan mencoba kembali.', 'danger')
      }
    }
  }

  return {
    toast,
    toastContainer,
    statusFetchProducts,
    currentPageProducts,
    limitPageProducts,
    totalRowsProducts,
    productStatus,
    searchKeyword,
    products,
    fetchProducts,
    changeLimitPageProducts,
    changeCurrentPageProducts,
    changeSearchKeyword,
    enterSearchKeyword,
    changeFilterProductStatus,
    openCreateForm,
    openEditForm,
    isOpenModalForm,
    categoriesProduct,
    initialForm,
    statusForm,
    stateForm,
    closeForm,
    submitForm,
  }
}
